'use client';

import React from 'react';
import { Guitar, DollarSign, Music2, AlertTriangle, Activity } from 'lucide-react';

// Backend integration point: replace mock data with API call to /api/collection/stats
const kpiData = {
  totalGuitars: 12,
  totalGuitarsDelta: '+2 this year',
  collectionValue: 28450,
  collectionValueDelta: '+$3,200 since Jan',
  brandsRepresented: 7,
  brandsList: 'Fender, Gibson, PRS, Martin, Taylor, Ibanez, Gretsch',
  overdueMaintenanceCount: 3,
  overdueGuitars: ['1959 Les Paul Jr.', 'Strat MIM', 'SG Special'],
  stringChangesThisYear: 18,
  stringChangesDelta: '4 this month',
};

export default function KPIBentoGrid() {
  return (
    // Grid plan: 5 cards → grid-cols-4 → row 1: hero spans 2 cols + 2 regular | row 2: 1 card spans 2 cols + 1 card spans 2 cols (last row fills evenly)
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-4 gap-4">
      {/* Hero: Total Guitars — spans 2 cols */}
      <div className="sm:col-span-2 lg:col-span-2 xl:col-span-2 2xl:col-span-2 bg-primary rounded-2xl p-6 relative overflow-hidden shadow-card">
        <div className="absolute inset-0 blob-primary pointer-events-none" />
        <div className="relative z-10">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[11px] font-600 uppercase tracking-widest text-primary-foreground/70">
                Total Guitars
              </p>
              <p className="text-5xl font-800 text-primary-foreground tabular-nums mt-2 leading-none">
                {kpiData?.totalGuitars}
              </p>
              <p className="text-sm text-primary-foreground/80 mt-2 font-500">{kpiData?.totalGuitarsDelta}</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-primary-foreground/15 flex items-center justify-center">
              <Guitar size={22} className="text-primary-foreground" />
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-1.5">
            {kpiData?.brandsList?.split(', ')?.map((b) => (
              <span key={`brand-tag-${b}`} className="text-[10px] font-600 px-2 py-0.5 rounded-full bg-primary-foreground/15 text-primary-foreground/90">
                {b}
              </span>
            ))}
          </div>
        </div>
      </div>
      {/* Collection Value */}
      <div className="bg-card rounded-2xl p-5 shadow-card border border-border hover:shadow-card-hover transition-shadow duration-200">
        <div className="flex items-start justify-between mb-3">
          <p className="text-[11px] font-600 uppercase tracking-widest text-muted-foreground">
            Collection Value
          </p>
          <div className="w-8 h-8 rounded-lg bg-positive/10 flex items-center justify-center">
            <DollarSign size={16} className="text-positive" />
          </div>
        </div>
        <p className="text-3xl font-700 text-foreground tabular-nums">
          ${kpiData?.collectionValue?.toLocaleString('en-US')}
        </p>
        <p className="text-xs text-positive font-500 mt-1.5">{kpiData?.collectionValueDelta}</p>
      </div>
      {/* Brands Represented */}
      <div className="bg-card rounded-2xl p-5 shadow-card border border-border hover:shadow-card-hover transition-shadow duration-200">
        <div className="flex items-start justify-between mb-3">
          <p className="text-[11px] font-600 uppercase tracking-widest text-muted-foreground">
            Brands
          </p>
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <Music2 size={16} className="text-primary" />
          </div>
        </div>
        <p className="text-3xl font-700 text-foreground tabular-nums">
          {kpiData?.brandsRepresented}
        </p>
        <p className="text-xs text-muted-foreground font-500 mt-1.5">Unique manufacturers</p>
      </div>
      {/* Overdue Maintenance — ALERT STATE */}
      <div className="sm:col-span-1 lg:col-span-2 xl:col-span-2 2xl:col-span-2 bg-negative/5 rounded-2xl p-5 shadow-card border border-negative/20 hover:shadow-card-hover transition-shadow duration-200">
        <div className="flex items-start justify-between mb-3">
          <p className="text-[11px] font-600 uppercase tracking-widest text-negative/80">
            Overdue Maintenance
          </p>
          <div className="w-8 h-8 rounded-lg bg-negative/10 flex items-center justify-center">
            <AlertTriangle size={16} className="text-negative" />
          </div>
        </div>
        <p className="text-3xl font-700 text-negative tabular-nums">
          {kpiData?.overdueMaintenanceCount}
        </p>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {kpiData?.overdueGuitars?.map((g) => (
            <span key={`overdue-${g}`} className="text-[10px] font-600 px-2 py-0.5 rounded-full bg-negative/10 text-negative">
              {g}
            </span>
          ))}
        </div>
        <p className="text-xs text-negative/70 font-500 mt-2">No maintenance logged in 6+ months</p>
      </div>
      {/* String Changes This Year */}
      <div className="sm:col-span-1 lg:col-span-2 xl:col-span-2 2xl:col-span-2 bg-card rounded-2xl p-5 shadow-card border border-border hover:shadow-card-hover transition-shadow duration-200">
        <div className="flex items-start justify-between mb-3">
          <p className="text-[11px] font-600 uppercase tracking-widest text-muted-foreground">
            String Changes
          </p>
          <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
            <Activity size={16} className="text-accent" />
          </div>
        </div>
        <p className="text-3xl font-700 text-foreground tabular-nums">
          {kpiData?.stringChangesThisYear}
        </p>
        <p className="text-xs text-muted-foreground font-500 mt-1.5">{kpiData?.stringChangesDelta} · 2026 total</p>
      </div>
    </div>
  );
}